import { useEffect, useMemo } from 'react';
import { Card, DatePicker, Divider, Flex, Form, Statistic, Typography } from 'antd';
import { useGetTripsLoadAggregatedStatistics } from 'api/statistic/use-get-trips-load-aggregated-statistics';
import { useGetManyWarehouses } from 'api/warehouses/use-get-many-warehouses';
import { useAuthStore } from 'shared/stores';
import { useFleetInsightsFiltersStore } from 'shared/stores/use-fleet-insights-filters';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters';
import { CompanyStats } from 'components/insights/company-stats';
import { OverdueStat } from 'components/insights/overdue-stat';
import { TransitTimeStat } from 'components/insights/transit-time-stat';
import styles from './fleet-insights.module.scss';

const { Title } = Typography;

export const FleetInsights = () => {
  const { user } = useAuthStore();
  const { filters, setFilters } = useFleetInsightsFiltersStore();
  const [form] = Form.useForm();

  const { data: warehouses } = useGetManyWarehouses({ withDeprecated: false, internalOnly: true });

  const handleSuccess = () => {
    transformAndSetFilters(form, setFilters);
  };

  useEffect(() => {
    if (filters.warehouseId || !warehouses?.length) return;

    form.setFieldValue('warehouseId', warehouses[0].id);

    handleSuccess();
  }, [warehouses]);

  const monthRange = useMemo(() => [filters.date.startOf('month'), filters.date.endOf('month')], [filters.date]);

  const { data: loadData } = useGetTripsLoadAggregatedStatistics({
    filters: { date: monthRange, userId: user?.id },
  });

  return (
    <Form form={form} initialValues={filters}>
      <Flex gap={16} vertical>
        <Form.Item name='date' className={styles.dateInput}>
          <DatePicker picker='month' allowClear={false} onChange={handleSuccess} />
        </Form.Item>

        <div>
          <Title level={5}>Personal</Title>
          <Flex gap={16} wrap='wrap'>
            <Card>
              <Statistic title='Average Load' value={loadData?.value?.averageLoad ?? 0} suffix='%' precision={1} />
            </Card>
            <Card>
              <Statistic
                title='Average Useful Points'
                value={loadData?.value?.averageUsefulPointsCount ?? 0}
                precision={1}
              />
            </Card>
          </Flex>
        </div>

        <div>
          <Title level={5}>Company</Title>
          <Flex gap={16} wrap='wrap' align='flex-end'>
            <CompanyStats date={monthRange} onChange={handleSuccess} />
            <Divider type='vertical' className={styles.divider} />
            <TransitTimeStat date={monthRange} onChange={handleSuccess} />
            <Divider type='vertical' className={styles.divider} />
            <OverdueStat date={monthRange} />
          </Flex>
        </div>
      </Flex>
    </Form>
  );
};
